import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ButtonFilled from "../../components/FormElements/ButtonFilled";
import styles from "./whatsapp.module.scss";

const BUCKETS = ["otp", "transactional", "expiry", "marketing"];
const BUCKET_LABEL_KEY = {
  otp: "NSRouting.bucketOTP",
  transactional: "NSRouting.bucketTransactional",
  expiry: "NSRouting.bucketExpiry",
  marketing: "NSRouting.bucketMarketing",
};

const EVENT_AUTO_NOTIFICATION = "marketing.auto_notification";
const EVENT_REVIEW = "marketing.review";
const EVENT_RFM = "marketing.rfm";

const RoutingMatrix = ({ channels, routing, onSave }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => normalizeBuckets(routing));
  const [eventDraft, setEventDraft] = useState(() => routing?.event_overrides || {});
  const [saveState, setSaveState] = useState("idle");

  const channelOptions = useMemo(() => buildChannelOptions(channels), [channels]);
  const allDisabled = channelOptions.length === 0;

  const transactionalPrimary = draft.transactional?.primary || "";
  const reviewChannelLabel = transactionalPrimary
    ? channelOptions.find((c) => c.id === transactionalPrimary)?.label || transactionalPrimary
    : t("NSRouting.useDefault");

  const handleChange = (bucket, value) => {
    setDraft((prev) => ({ ...prev, [bucket]: { primary: value } }));
  };

  const handleEventChange = (eventType, value) => {
    setEventDraft((prev) => ({ ...prev, [eventType]: { primary: value } }));
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaveState("saving");
    try {
      const event_overrides = {
        ...eventDraft,
        [EVENT_REVIEW]: { primary: transactionalPrimary },
      };
      await onSave({ buckets: draft, event_overrides });
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch (e) {
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 3500);
    }
  };

  return (
    <div className={styles.routingMatrix}>
      <h2>{t("NSWhatsapp.v2RoutingHeader")}</h2>
      <table className={styles.routingTable}>
        <tbody>
          {BUCKETS.map((bucket) => (
            <tr key={bucket}>
              <th>{t(BUCKET_LABEL_KEY[bucket])}</th>
              <td>
                <select
                  value={draft[bucket]?.primary || ""}
                  onChange={(e) => handleChange(bucket, e.target.value)}
                  disabled={allDisabled}
                >
                  <option value="">{t("NSRouting.useDefault")}</option>
                  {channelOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {allDisabled && (
                  <span className={styles.routingHint}>
                    {t("NSRouting.channelDisabledHint")}
                  </span>
                )}
              </td>
            </tr>
          ))}

          <tr key={EVENT_AUTO_NOTIFICATION}>
            <th>{t("NSRouting.eventAutoNotification")}</th>
            <td>
              <select
                value={eventDraft[EVENT_AUTO_NOTIFICATION]?.primary || ""}
                onChange={(e) => handleEventChange(EVENT_AUTO_NOTIFICATION, e.target.value)}
                disabled={allDisabled}
              >
                <option value="">{t("NSRouting.useDefault")}</option>
                {channelOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </td>
          </tr>

          <tr key={EVENT_RFM}>
            <th>{t("NSRouting.eventRFM")}</th>
            <td>
              <select
                value={eventDraft[EVENT_RFM]?.primary || ""}
                onChange={(e) => handleEventChange(EVENT_RFM, e.target.value)}
                disabled={allDisabled}
              >
                <option value="">{t("NSRouting.useDefault")}</option>
                {channelOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </td>
          </tr>

          <tr key={EVENT_REVIEW}>
            <th>{t("NSRouting.eventReview")}</th>
            <td>
              <span>{reviewChannelLabel}</span>
              <span className={styles.routingHint}>
                {t("NSRouting.reviewCoupledHint")}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div className={styles.routingActions}>
        <ButtonFilled onClick={handleSave} disabled={allDisabled || saveState === "saving"}>
          {saveState === "saving" ? t("NSRouting.saving") : t("NSRouting.saveButton")}
        </ButtonFilled>
      </div>

      {saveState === "saved" && (
        <div className={styles.routingToastSuccess}>{t("NSRouting.savedToast")}</div>
      )}
      {saveState === "error" && (
        <div className={styles.routingToastError}>{t("NSRouting.errorToast")}</div>
      )}
    </div>
  );
};

function buildChannelOptions(channels) {
  if (!channels) return [];
  return ["channel_1", "channel_2"]
    .filter((slot) => channels[slot]?.enabled && channels[slot]?.connection_state === "connected")
    .map((slot) => ({
      id: slot,
      label: channels[slot]?.display_name || slot,
    }));
}

function normalizeBuckets(routing) {
  const seed = {};
  for (const bucket of BUCKETS) {
    seed[bucket] = routing?.buckets?.[bucket] || { primary: "" };
  }
  return seed;
}

export default RoutingMatrix;
